import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnChanges, Renderer2 } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, FormGroupDirective, Validators } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TagLists } from 'app/models/cx-interaction/cx-interaction-tag-list.interface';
import { environment } from 'environments/environment';
import { Select } from '@ngxs/store';
import { CXInteractionState } from 'app/store/cx-interaction/cx-interaction.state';
import { Observable } from 'rxjs';
import { Alert } from 'app/models/dto/alert.model';

const baseUrl = `${environment.API_URL}`;
@Component({
    selector: 'app-cx-interaction-form',
    templateUrl: 'cx-interaction-form.component.html',
    styleUrls: ['cx-interaction-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CxInteractionFormComponent implements OnChanges, OnInit{

    @Select(CXInteractionState.alerts) alerts$: Observable<Alert[]>;

    @Output() saveCxInteraction = new EventEmitter<any>();
    @Output() cxDrawerClose = new EventEmitter<boolean>();
    @Input() tagOptions: Array<any>;

    @Input() formSaving: boolean = false;

    tagsDisabled: boolean = true;
    tagsRequired: boolean = true;
    borrowerIdRequired: boolean = true;
    model: any = {};
    processing = false;

    initialSentimentOptions: Array<object>;
    interactionTypeOptions: Array<object>;
    generalInquiryOptions: Array<object>;
    troubleshootingOptions: Array<object>;
    departmentRequiredOptions: Array<object>;
    otherOptions: Array<object>;
    complaintOptions: Array<object>;
    funnelOptions: Array<object>;
    outcomeOptions: Array<object>;
    appStatusOptions: Array<object>;
    tagLists: TagLists[];
    saveTagValues: Array<any> = [];
    options: FormlyFormOptions = {};
    cxInteractionInfo: object = {};
    fields: FormlyFieldConfig[] = [];
    cxInteractionForm: UntypedFormGroup;

    constructor(
        private fb: UntypedFormBuilder,
        private renderer: Renderer2,
    ) {
 
        this.initialSentimentOptions = [
          {value: 'Positive', label: 'Positive'},
          {value: 'Neutral', label: 'Neutral'},
          {value: 'Negative', label: 'Negative'},
          {value: 'Alerted TL', label: 'Alerted TL'},
        ];

        this.interactionTypeOptions = [
          {value: 'CX Inbound Call', label: 'Inbound Call'},
          {value: 'CX Outbound Call', label: 'Outbound Call'},
          {value: 'CX Chat', label: 'Chats'},
          {value: 'CX Email', label: 'Email'},
          {value: 'CX Social Media', label: 'Social Media'},
        ];

        this.funnelOptions = [
          {
            value: 'No account created', 
            label: 'No account created'
          },
          {
            value: 'Incomplete Lendio package', 
            label: 'Incomplete Lendio package'
          },
          {
            value: 'Lendio application and documentation completed',
            label: 'Lendio application and documentation completed'
          },
          {
            value: 'Automated application submit to lender', 
            label: 'Automated application submit to lender'
          },
          {
            value: 'Rep assisted application submit to lender', 
            label: 'Rep assisted application submit to lender'
          },
          {
            value: 'Received Funds', 
            label: 'Received Funds'
          },
          {
            value: 'Sunrise', 
            label: 'Sunrise'
          },
          {
            value: 'Renewals incomplete Lendio package', 
            label: 'Renewals incomplete Lendio package'
          },
          {
            value: 'Renewals Lendio application and documentation completed', 
            label: 'Renewals Lendio application and documentation completed'
          },
          {
            value: 'Renewal automated application submit to lender', 
            label: 'Renewal automated application submit to lender'
          },
          {
            value: 'Renewal Rep assisted application submit to lender', 
            label: 'Renewal Rep assisted application submit to lender'
          },
          {
            value: 'Renewals Received Funds', 
            label: 'Renewals Received Funds'
          },
        ];

        this.outcomeOptions = [
          {value: 'Issue Resolved', label: 'Issue Resolved'},
          {value: 'Unresolved Issue', label: 'Unresolved Issue'},
          {value: 'Resolved Troubleshooting Issue', label: 'Resolved Troubleshooting Issue'},
          {value: 'Unresolved/Reported Troubleshooting Issue', label: 'Unresolved/Reported Troubleshooting Issue'},
          {value: 'Workaround for Troubleshooting Issue', label: 'Workaround for Troubleshooting Issue'},
          {value: 'Spam', label: 'Spam'},
          {value: 'No Contact', label: 'No Contact'},
        ];

        this.appStatusOptions = [
          {value: 'Completed application', label: 'Completed application'},
          {value: 'Completed application with bank statements', label: 'Completed application with bank statements'},
          {value: 'Not applicable', label: 'Not applicable'},
        ];
    }

    ngOnInit(): void {
      this.alerts$.subscribe((alert) => {
        if (!alert.length) {
          return;
        }
        this.scrollToTop();
      });
      this.generateFormlyFields();
    }

    generateFormlyFields() {
      this.fields = [
        {
          fieldGroupClassName: 'tw-block',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-h-14',
              fieldGroup: [
                {
                  key: 'no_borrower_id',
                  className: 'tw-mr-2',
                  defaultValue: false,
                  type: 'checkbox',
                  templateOptions: {
                    change: this.noBorrowerIdChanged.bind(this),
                    label: 'No Borrower ID',
                    appearance: 'legacy'
                  }
                },
                {
                  key: 'borrower_id_label',
                  type: 'padded-label',
                  className: 'tw-my-auto tw-pb-2 tw-flex-1 tw-h-3 tw-font-normal tw-text-slate-400',
                  templateOptions: {
                     label: 'All fields required',
                  },
                },
              ]
            },
            {
              key: 'borrower_id',
              className: 'loans-field',
              type: 'text',
              templateOptions: {
                label: 'BID #',
                hideRequiredMarker: true,
                placeholder: 'Borrower ID Number',
                floatLabel: 'always',
                appearance: 'outline',
                required: this.borrowerIdRequired,
                pattern: /^-?(0|[1-9]\d*)?$/,
                disabled: this.borrowerIdRequired ? false : true
              }
            },
            {
              key: 'interaction_type',
              type: 'select',
              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Interaction Type',
                placeholder: 'Select Interaction Type',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                multiple: false,
                options: this.interactionTypeOptions,
                change: this.onInteractionTypeChange.bind(this),
              },
            },
          ]
        },
        {
          fieldGroupClassName: 'tw-block tw-mt-2',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-mt-3 tw-mb-4 tw-h-4',
              fieldGroup: [
                {
                  key: 'beginning_label',
                  type: 'padded-label',
                  className: 'tw-my-auto tw-pb-2 tw-font-semibold',
                  templateOptions: {
                    label: 'At the beginning of the interaction',
                  },
                },
                {
                  key: 'beginning_label_required',
                  type: 'padded-label',
                  className: 'tw-my-auto tw-pb-2 tw-font-normal tw-text-slate-400 tw-ml-2',
                  templateOptions: {
                    label: 'All fields required',
                  },
                },
              ]
            },
            {
              key: 'initial_sentiment',
              type: 'select',
              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Initial Borrower Sentiment',
                placeholder: 'What was the borrower\'s initial sentiment?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                multiple: false,
                options: this.initialSentimentOptions,
              },
            },
            {
              key: 'funnel',
              type: 'select',
              className: 'mat-select-value-override tw-max-w-fit',
              templateOptions: {
                appearance: 'outline',
                label: 'Part of the Funnel',
                placeholder: 'What part of the funnel was the borrower in?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                options: this.funnelOptions,
              },
            },
          ]
        },
        {
          fieldGroupClassName: 'tw-block tw-mt-2',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-mt-3 tw-mb-4 tw-h-4',
              fieldGroup: [
                {
                  key: 'conclusion_label',
                  type: 'padded-label',
                  className: 'tw-my-auto tw-pb-2 tw-font-semibold',
                  templateOptions: {
                    label: 'At the conclusion of the interaction',
                  },
                },
                {
                  key: 'conclusion_label_required',
                  type: 'padded-label',
                  className: 'tw-my-auto tw-pb-2 tw-font-normal tw-text-slate-400 tw-ml-2',
                  templateOptions: {
                    label: 'All fields required',
                  },
                },
              ]
            },
            {
              key: 'outcome',
              type: 'select',
              
              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Outcome',
                placeholder: 'How or was the borrower\'s needs met?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                options: this.outcomeOptions,
              },
            },
            {
              key: 'appStatus',
              type: 'select',
              
              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Assist with Application',
                placeholder: 'Did you assist the borrower with their application?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                options: this.appStatusOptions,
              },
            },
            {
              key: 'note',
              type: 'textarea-auto-resize',
              templateOptions: {
                appearance: 'outline',
                label: 'Note',
                placeholder: 'Leave a detailed note',
                floatLabel: 'always',
                required: true,
                hideRequiredMarker: true,
              },
            },
          ]
        },
        {
          fieldGroupClassName: 'tw-block tw-mt-2',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-mt-3 tw-mb-4 tw-h-4',
                fieldGroup: [
                  {
                    key: 'purpose_label',
                    type: 'padded-label',
                    className: 'tw-my-auto tw-pb-2 tw-font-semibold',
                    templateOptions: {
                      label: 'Interaction Tags',
                    },
                  },
                  {
                    key: 'purpose_label',
                    type: 'padded-label',
                    className: 'tw-my-auto tw-pb-2 tw-font-normal tw-text-slate-400 tw-ml-2',
                    templateOptions: {
                      label: 'Select all that apply',
                    },
                  },
                ]
              },
              {
                key: 'general_inquiry',
                type: 'select',
                
                className: 'mat-select-value-override',
                templateOptions: {
                  appearance: 'outline',
                  label: 'General Inquiries',
                  placeholder: 'General Inquiries categories',
                  hideRequiredMarker: true,
                  floatLabel: 'always',
                  required: this.tagsRequired,
                  multiple: true,
                  hideExpression: true,
                  options: this.generalInquiryOptions,
                  disabled: this.tagsDisabled,
                  change: this.onTagChange.bind(this),
                },
              },
              {
                  key: 'trouble_shooting',
                  type: 'select',
                  
                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Troubleshooting',
                    placeholder: 'Troubleshooting Issues categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.troubleshootingOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
              {
                  key: 'department_required',
                  type: 'select',
                  
                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Requires Another Dept',
                    placeholder: 'Requires Another Dept. categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.departmentRequiredOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
              {
                  key: 'other',
                  type: 'select',
                  
                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Other',
                    placeholder: 'Other categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.otherOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
              {
                  key: 'complaints',
                  type: 'select',
                  
                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Complaints',
                    placeholder: 'Complaint categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.complaintOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
          ]
        },
      ];
    }

    ngOnChanges() {
        this.cxInteractionForm = this.fb.group({
  
        });
    }


    onSaveCxInteraction(formDirective: FormGroupDirective) {
      if (!this.cxInteractionForm.valid) {
        return;
      }
      this.processing = true;
      this.saveTagValues = [];
      this.setTagValue(this.cxInteractionForm.value.general_inquiry);
      this.setTagValue(this.cxInteractionForm.value.trouble_shooting);
      this.setTagValue(this.cxInteractionForm.value.department_required);
      this.setTagValue(this.cxInteractionForm.value.other);
      this.setTagValue(this.cxInteractionForm.value.complaints);
      if (!this.cxInteractionForm.value.no_borrower_id) {
        this.setCxInteractionObject('borrowerId', this.cxInteractionForm.value.borrower_id);
      } else {
        this.setCxInteractionObject('borrowerId', null);
      }
      this.setCxInteractionObject('interactionType', this.removeCxString(this.cxInteractionForm.value.interaction_type));
      this.setCxInteractionObject('sentiment', this.cxInteractionForm.value.initial_sentiment);
      this.setCxInteractionObject('note', this.cxInteractionForm.value.note);
      this.setCxInteractionObject('funnelStatus', this.cxInteractionForm.value.funnel);
      this.setCxInteractionObject('interactionOutcome', this.cxInteractionForm.value.outcome);
      this.setCxInteractionObject('appStatus', this.cxInteractionForm.value.appStatus);
      this.setCxInteractionObject('tags', this.saveTagValues);
      this.saveCxInteraction.emit({
        'info': this.cxInteractionInfo,
        'form': this.cxInteractionForm,
        'directive': formDirective,
        'cxComponent': this
      });
    }

    setCxInteractionObject(key, value) {
      this.cxInteractionInfo[key] = value;
    }

    setTagValue(tagValues){
      if (tagValues) {
        tagValues.map((value) => {
          this.saveTagValues.push(value);
        });
      }
    }

    removeCxString(string) {
      const indexOfSpace = string.indexOf(' ');
      return string.substring(indexOfSpace + 1);
    }

    onTagChange(event) {      
      const generalValue = this.cxInteractionForm.value.general_inquiry ?? [];
      const troubleValue = this.cxInteractionForm.value.trouble_shooting ?? [];
      const departmentValue = this.cxInteractionForm.value.department_required ?? [];
      const otherValue = this.cxInteractionForm.value.other ?? [];
      const complaintValue = this.cxInteractionForm.value.complaints ?? [];

      const valueLength = generalValue.length
        + troubleValue.length
        + departmentValue.length
        + otherValue.length
        + complaintValue.length;

      if (valueLength < 1) {
        //if no tags are selected, add required validation
        this.tagsRequired = true;
        this.cxInteractionForm.controls.general_inquiry.setValidators([Validators.required]);
        this.cxInteractionForm.controls.general_inquiry.updateValueAndValidity();
        this.cxInteractionForm.controls.trouble_shooting.setValidators([Validators.required]);
        this.cxInteractionForm.controls.trouble_shooting.updateValueAndValidity();
        this.cxInteractionForm.controls.department_required.setValidators([Validators.required]);
        this.cxInteractionForm.controls.department_required.updateValueAndValidity();
        this.cxInteractionForm.controls.other.setValidators([Validators.required]);
        this.cxInteractionForm.controls.other.updateValueAndValidity();
        this.cxInteractionForm.controls.complaints.setValidators([Validators.required]);
        this.cxInteractionForm.controls.complaints.updateValueAndValidity();
      } else {
        this.tagsRequired = false;
        this.cxInteractionForm.controls.general_inquiry.clearValidators();
        this.cxInteractionForm.controls.general_inquiry.updateValueAndValidity();
        this.cxInteractionForm.controls.trouble_shooting.clearValidators();
        this.cxInteractionForm.controls.trouble_shooting.updateValueAndValidity();
        this.cxInteractionForm.controls.department_required.clearValidators();
        this.cxInteractionForm.controls.department_required.updateValueAndValidity();
        this.cxInteractionForm.controls.other.clearValidators();
        this.cxInteractionForm.controls.other.updateValueAndValidity();
        this.cxInteractionForm.controls.complaints.clearValidators();
        this.cxInteractionForm.controls.complaints.updateValueAndValidity();
      }      

    }
    
    resetDefaultFormSettings() {
      this.tagsRequired = true;
      this.tagsDisabled = true;
      this.borrowerIdRequired = true;
      this.generateFormlyFields();
      this.processing = false;
    }


    onInteractionTypeChange(event) {
        const type = this.cxInteractionForm.value.interaction_type.replace(/ /g,'_');

        //general inquiry options
        [this.tagOptions].filter((data) => {
          const genOpts = data[type].categories.generalInquiry.tags;
          this.generalInquiryOptions = genOpts.map((tag) => {
           return {
            label: tag['tagName'],
            value: tag['tagId']
           } ;
          });

          //troubleshooting options
          const trouble = data[type].categories.troubleshooting.tags;
          this.troubleshootingOptions = trouble.map((tag) => {
           return {
            label: tag['tagName'],
            value: tag['tagId']
           } ;
          });

          //complaint options
          const complaint = data[type].categories.complaint.tags;
          this.complaintOptions = complaint.map((tag) => {
           return {
            label: tag['tagName'],
            value: tag['tagId']
           } ;
          });

          //other options
          const other = data[type].categories.other.tags;
          this.otherOptions = other.map((tag) => {
           return {
            label: tag['tagName'],
            value: tag['tagId']
           } ;
          });

          //Department required options
          const department = data[type].categories.departmentRequired.tags;
          this.departmentRequiredOptions = department.map((tag) => {
           return {
            label: tag['tagName'],
            value: tag['tagId']
           } ;
          });
        });


        this.tagsDisabled = false;
        this.generateFormlyFields();
       
    }

    noBorrowerIdChanged(event) {
      const notRequired = this.cxInteractionForm.value.no_borrower_id;
      if (notRequired) {
        this.cxInteractionForm.get('borrower_id')?.reset();
        this.borrowerIdRequired = false;
      } else {
        this.borrowerIdRequired = true;
      }
      this.generateFormlyFields();
    }

    cancelButton(formDirective: FormGroupDirective) {
      formDirective.resetForm();
      this.cxInteractionForm.reset();
      this.resetDefaultFormSettings();
      this.cxDrawerClose.emit(true);
    }

  scrollToTop() {
    this.renderer.selectRootElement('#cxAlerts').scrollIntoView({ behavior: 'smooth' });
  }
}

